"use client";
import { useEffect, useState } from "react";
import { useAuth } from "@clerk/nextjs";
import { IconBolt } from "@tabler/icons-react";
import { useFiltersStore } from "@/stores/filtersStore";
import { useKurboAction } from "@/hooks/useKurboAction";
import styles from "./styles.module.scss";
import { Box as MantineBox, UnstyledButton as MantineUnstyledButton } from "@mantine/core";

type KurboButtonProps = {
  variant?: "nav" | "sidebar" | "toolbar";
};

const KurboButton = ({ variant = "nav" }: KurboButtonProps) => {
  const isToolbar = variant === "toolbar";
  const { isSignedIn } = useAuth();
  const kurboMode = useFiltersStore((state) => state.kurboMode);
  const setKurboMode = useFiltersStore((state) => state.actions.setKurboMode);
  const { handleKurboAction } = useKurboAction();
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    // Clear on successful sign-in.
    if (isSignedIn) {
      setSigningIn(false);
      return;
    }
    if (!signingIn) return;

    // When the Clerk modal is dismissed without signing in, isSignedIn stays
    // false so the effect above never fires. Instead, register a one-shot
    // document click listener (after a short delay to skip the triggering
    // click) — the next click anywhere resets the button to its idle state.
    let removeListener = () => {};
    const timer = setTimeout(() => {
      const handler = () => setSigningIn(false);
      document.addEventListener("click", handler, { once: true, capture: true });
      removeListener = () => document.removeEventListener("click", handler, { capture: true });
    }, 200);

    return () => {
      clearTimeout(timer);
      removeListener();
    };
  }, [signingIn, isSignedIn]);

  const handleClick = () => {
    if (kurboMode) {
      setKurboMode(false);
      return;
    }
    if (!isSignedIn) setSigningIn(true);
    handleKurboAction();
  };

  const label = signingIn
    ? isToolbar
      ? "SIGN IN"
      : "SIGN IN TO KURBO"
    : isToolbar
      ? "KURBO"
      : "KURBO";

  const title = signingIn
    ? "Sign in to use KURBO mode"
    : kurboMode
      ? "Turn off KURBO mode. Real-time listings from the last hour that match your filters."
      : "Turn on KURBO mode. Real-time listings from the last hour that match your filters.";

  return (
    <MantineUnstyledButton
      className={`${styles.kurboBtn} ${variant === "sidebar" ? styles.sidebar : ""} ${
        isToolbar ? styles.toolbar : ""
      }`}
      aria-label={title}
      aria-pressed={kurboMode}
      data-active={String(!signingIn && kurboMode)}
      data-signing-in={String(signingIn)}
      onClick={handleClick}
      title={isToolbar ? undefined : title}
      type="button"
    >
      {isToolbar && (
        <MantineBox component="span" className={styles.toolbarIconWrap} aria-hidden="true">
          <IconBolt className={styles.toolbarIcon} size={16} stroke={2} />
        </MantineBox>
      )}
      <MantineBox component="span" className={styles.toolbarLabel}>
        {label}
      </MantineBox>
      {isToolbar && (
        <MantineBox component="span" className={styles.toolbarToggle} aria-hidden="true">
          <MantineBox component="span" className={styles.toolbarToggleKnob} />
        </MantineBox>
      )}
    </MantineUnstyledButton>
  );
};

export default KurboButton;
